import { useState } from 'react'
import { X, Copy, Check, ExternalLink } from 'lucide-react'
import { generateRouteEmbedCode, generateRouteEmbedUrl } from '../../utils/embedHelpers'

interface RouteEmbedModalProps {
  isOpen: boolean
  onClose: () => void
  routeId: string
  routeName: string
}

export function RouteEmbedModal({ isOpen, onClose, routeId, routeName }: RouteEmbedModalProps) {
  const [embedOptions, setEmbedOptions] = useState({
    width: '100%',
    height: '700px',
    mobileMode: true, // Default to mobile mode for embeds
    theme: 'dark' as const,
    mapStyle: 'satellite' as const,
    showHeader: false,
    showSidebar: false,
    showElevation: true, // Need this for elevation to show at all
    showPhotos: true, // Need this for photos to show at all
    showPOIs: true, // Need this for POIs to show at all
    showFlyby: true,
    // New simple toggle options
    startElevationMinimized: false,
    startSegmentListMinimized: true, 
    startPhotosOff: false,
    startPOIsOff: false
  })
  const [copied, setCopied] = useState(false)

  if (!isOpen) return null

  const embedCode = generateRouteEmbedCode(routeId, embedOptions)
  const embedUrl = generateRouteEmbedUrl(routeId, embedOptions)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  const updateEmbedOption = (key: string, value: any) => {
    setEmbedOptions(prev => ({ ...prev, [key]: value }))
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 sticky top-0 bg-gray-900 z-10">
          <div>
            <h2 className="text-lg font-semibold text-white">Embed Route</h2>
            <p className="text-xs text-gray-400 mt-0.5">Share "{routeName}" on your website</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-gray-400 hover:text-white" />
          </button>
        </div>

        <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-800" style={{ maxHeight: 'calc(85vh - 60px)' }}>
          {/* Configuration and Embed Code */}
          <div className="flex flex-col lg:flex-row">
            {/* Configuration Panel */}
            <div className="lg:w-1/2 p-4 border-r border-gray-700">
              <div className="space-y-4">
                {/* Simple Configuration Options */}
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-2">
                    Initial State Options
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={embedOptions.startElevationMinimized}
                        onChange={(e) => updateEmbedOption('startElevationMinimized', e.target.checked)}
                        className="text-emerald-600 focus:ring-emerald-500 rounded w-4 h-4"
                      />
                      <div>
                        <div className="text-xs font-medium text-gray-200">Start with elevation minimized</div>
                        <div className="text-[10px] text-gray-500">Users can expand it if needed</div>
                      </div>
                    </label>
                    
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={embedOptions.startSegmentListMinimized}
                        onChange={(e) => updateEmbedOption('startSegmentListMinimized', e.target.checked)}
                        className="text-emerald-600 focus:ring-emerald-500 rounded w-4 h-4"
                      />
                      <div>
                        <div className="text-xs font-medium text-gray-200">Start with segment list collapsed</div>
                        <div className="text-[10px] text-gray-500">Users can expand to see route segments</div>
                      </div>
                    </label>
                    
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={embedOptions.startPhotosOff}
                        onChange={(e) => updateEmbedOption('startPhotosOff', e.target.checked)}
                        className="text-emerald-600 focus:ring-emerald-500 rounded w-4 h-4"
                      />
                      <div>
                        <div className="text-xs font-medium text-gray-200">Start with photos disabled</div>
                        <div className="text-[10px] text-gray-500">Users can enable photos if they want</div>
                      </div>
                    </label>
                    
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={embedOptions.startPOIsOff}
                        onChange={(e) => updateEmbedOption('startPOIsOff', e.target.checked)}
                        className="text-emerald-600 focus:ring-emerald-500 rounded w-4 h-4"
                      />
                      <div>
                        <div className="text-xs font-medium text-gray-200">Start with POIs disabled</div>
                        <div className="text-[10px] text-gray-500">Users can enable points of interest if they want</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Size Configuration */}
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-2">
                    Size
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Width</label>
                      <input
                        type="text"
                        value={embedOptions.width}
                        onChange={(e) => updateEmbedOption('width', e.target.value)}
                        className="w-full px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="100%"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Height</label>
                      <input
                        type="text"
                        value={embedOptions.height}
                        onChange={(e) => updateEmbedOption('height', e.target.value)}
                        className="w-full px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="600px"
                      />
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Code Panel */}
            <div className="lg:w-1/2 p-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-medium text-gray-300">
                    Embed Code
                  </label>
                  <button
                    onClick={handleCopy}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors ${
                      copied
                        ? 'bg-green-900/50 text-green-400 border border-green-700'
                        : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700'
                    }`}
                  >
                    {copied ? (
                      <>
                        <Check className="w-3 h-3" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        Copy Code
                      </>
                    )}
                  </button>
                </div>

                <textarea
                  value={embedCode}
                  readOnly
                  className="w-full p-2 text-xs font-mono bg-gray-800 border border-gray-700 rounded text-gray-300 resize-none focus:ring-emerald-500 focus:border-emerald-500"
                  style={{ height: '150px' }}
                />

                <div className="p-2 bg-blue-900/20 border border-blue-800/50 rounded">
                  <p className="text-[10px] text-blue-400">
                    <strong>Tip:</strong> Copy this code and paste it into your website's HTML where you want the route to appear.
                  </p>
                </div>
              </div>
            </div>
          </div>

          
          {/* Compact Preview */}
          <div className="p-4 border-t border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-300">
                Live Preview
              </label>
              <a
                href={embedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-200 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Open fullscreen
              </a>
            </div>
            <div 
              className="rounded-lg border border-gray-700 overflow-hidden bg-gray-950"
              style={{ height: embedOptions.height || '600px' }}
            >
              <iframe
                src={embedUrl}
                width="100%"
                height="100%"
                frameBorder="0"
                className="w-full h-full"
                style={{ border: 0 }}
              />
            </div>
            {/* Extra padding to ensure preview is fully visible */}
            <div className="h-20"></div>
          </div>
        </div>
      </div>
    </div>
  )
}